// ---------------------------------------------------------------------------
// The letters reveal: one panel that builds itself into a leaderboard.
//
// The whole of a letters round happens in this file. Answers land in the panel
// one at a time, shortest declared first, each inserted at the top with the
// rows below sliding down — see the argument for that shape at the head of
// `RevealScreen.tsx`, which is where somebody opening the screen will find it.
//
// Everything else here serves that one panel and has no other caller. The
// `LEADER_*` tables are the row scale, and they are keyed by seat count rather
// than by how many rows are out, because a row already read must never resize.
// `useInsertion` is the FLIP that makes the rows move. `RulingControl` is the
// host's overrule, which is on the row rather than on a slide precisely so it
// stays reachable while the panel keeps growing.
//
// Only the panel is exported. The four things under it are its internals, and a
// second caller for any of them would be a second thing pretending to be this
// list.
// ---------------------------------------------------------------------------

import { useLayoutEffect, useRef } from 'react';
import type { PublicPlayer } from '@countdown/shared';
import { Button, IconButton } from '../../../components/primitives/Button.js';
import { CheckIcon, CloseIcon, Icon } from '../../../components/primitives/Icon.js';
import { PlayerChip } from '../../../components/primitives/PlayerChip.js';
import { Frame } from '../../../components/round/atoms.js';
import { displayColorOf } from '../../../lib/playerLookup.js';
import { prefersReducedMotion } from '../../../store/settingsStore.js';
import { AskableWord, ReasonChip, STAGE_FILL } from './atoms.js';
import { ReasoningNote } from './frames.js';
import { ruledOutLabel, rulingOf } from './ruling.js';
import type { Ruling } from './ruling.js';
import type { RevealView } from './view.js';

// --- The letters reveal, which is one list that builds itself ---------------

/**
 * How long a row takes to get out of the way of the one being inserted above
 * it. Short: this is the room noticing that the list moved, not a performance.
 * The pacing between insertions is the server's and lives in shared constants.
 */
const INSERT_MS = 320;

type RowSize = 'sm' | 'md' | 'lg';

const LEADER_ROW: Record<RowSize, string> = {
  sm: 'gap-2 px-2.5 py-1',
  md: 'gap-2.5 px-3 py-1.5',
  lg: 'gap-3 px-3.5 py-2.5',
};

const LEADER_WORD: Record<RowSize, string> = {
  sm: 'text-[15px] sm:text-[18px]',
  md: 'text-[19px] sm:text-[23px]',
  lg: 'text-[23px] sm:text-[28px]',
};

/**
 * The score, said once. It is the biggest thing on the row after the word,
 * because it is the one number the room is waiting on.
 */
const LEADER_SCORE: Record<RowSize, string> = {
  sm: 'text-[17px]',
  md: 'text-[20px]',
  lg: 'text-[24px]',
};

/** The chip owns the name and its badge, so the row never sizes a name itself. */
const LEADER_CHIP: Record<RowSize, 'sm' | 'md' | 'lg'> = { sm: 'sm', md: 'sm', lg: 'md' };

/**
 * Slides the rows below an insertion down, by the FLIP the board shuffle uses.
 *
 * Measure where every row is *after* React has laid the new one out, compare
 * against where it was on the last commit, and animate the difference away.
 * Transforms only, so nothing here can reflow the panel or move the control bar
 * under a thumb, and an environment with no animation API simply shows the new
 * order. A row with no previous position is the one that just landed: it
 * arrives rather than moves.
 *
 * Keyed off the ids in order rather than off an array identity, so it does not
 * re-measure on every unrelated render of the screen.
 */
function useInsertion(rowKey: string) {
  const listRef = useRef<HTMLUListElement | null>(null);
  const previous = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;

    const before = previous.current;
    const after = new Map<string, number>();
    const still = prefersReducedMotion();

    for (const node of Array.from(list.children)) {
      const row = node as HTMLElement;
      const playerId = row.dataset.playerId;
      if (playerId === undefined) continue;

      const top = row.getBoundingClientRect().top;
      after.set(playerId, top);
      if (still || typeof row.animate !== 'function') continue;

      const from = before.get(playerId);
      if (from === undefined) {
        row.animate(
          [
            { opacity: 0, transform: 'translateY(-8px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: INSERT_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
        );
        continue;
      }

      const shift = from - top;
      if (Math.abs(shift) < 1) continue;
      row.animate(
        [{ transform: `translateY(${shift}px)` }, { transform: 'translateY(0)' }],
        { duration: INSERT_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      );
    }

    previous.current = after;
  }, [rowKey]);

  return listRef;
}

/**
 * The host's overrule, on the row rather than on a slide.
 *
 * **One control at a time, and which one depends on whether a ruling is already
 * in force.** With none, it is a single button saying what pressing it would do
 * rather than offering to confirm what is already true: a good word can be
 * disallowed and a ruled-out one can be allowed. With a ruling in force, the row
 * already says so in a chip and the only thing left to offer is taking it back —
 * so the control becomes `Undo`, which is what the host asked for. A toggle in
 * its place would offer to rule the other way, and "rule the other way" is not
 * the same act as "I pressed that by mistake": it leaves a ruling on the word.
 *
 * Both names carry the word and the player, because there are up to sixteen of
 * these on the screen at once and "Allow it" sixteen times names nothing. The
 * undo's visible label is one word and its accessible name begins with that same
 * word, so the two agree.
 *
 * **Nothing is offered at all unless the room turned rulings on.** They are a
 * setting, and switching it on costs the game its record — see `gameIsRecorded`
 * — so a room that left it off must not be shown a button that would quietly
 * spend that. The server refuses the action either way; a button it refuses is a
 * button that promises the host something they do not have.
 */
function RulingControl({
  view,
  player,
  answer,
  ruling,
}: {
  view: RevealView;
  player: PublicPlayer;
  answer: string;
  ruling: Ruling;
}) {
  // Nothing to rule on when there is nothing there. A row of dashes with a
  // button on it invites a press that cannot mean anything.
  if (!view.state.settings.hostRulings) return null;
  if (!view.state.youAreHost || answer.length === 0) return null;

  if (ruling.host !== null) {
    return (
      <Button
        size="sm"
        variant="outline"
        aria-label={`Undo the ruling on ${answer} from ${player.displayName}`}
        className="ml-1 px-2.5"
        onClick={() => view.onUndoRuling(player.id)}
      >
        Undo
      </Button>
    );
  }

  const allow = !ruling.allowed;

  return (
    <IconButton
      size="sm"
      variant={allow ? 'good' : 'danger'}
      label={`${allow ? 'Allow' : 'Disallow'} ${answer} from ${player.displayName}`}
      className="ml-1"
      onClick={() => view.onOverride(player.id, allow)}
    >
      <Icon icon={allow ? CheckIcon : CloseIcon} size={16} />
    </IconButton>
  );
}

/**
 * The letters reveal: one panel, and every answer the server has let out.
 *
 * Answers come shortest declared first and each is inserted at the **top**, so
 * the rows below slide down and the best answer of the round ends up on top.
 * The ordering is the design: the panel assembles itself into a leaderboard in
 * front of the room, rather than being a list that has to be re-sorted
 * afterwards to mean anything.
 *
 * The row that just landed is spotlit. `released` is in release order, so that
 * row is always the last id and always the first row — nothing here has to
 * remember anything to know which one is new.
 *
 * The row size is fixed by the number of seats and not by the number of rows so
 * far. Sizing on what is on screen would shrink every row already read the
 * moment the fifth one landed, which is the one kind of movement this panel
 * must not have.
 */
export function LettersLeaderboard({ view }: { view: RevealView }) {
  const { ordered, released, state } = view;
  const byId = new Map(ordered.map((player) => [player.id, player]));

  const rows = [...released]
    .reverse()
    .map((playerId) => byId.get(playerId))
    .filter((player): player is PublicPlayer => player !== undefined)
    .map((player) => {
      const submission = view.submissionOf(player.id);
      const ruling: Ruling =
        submission === undefined
          ? { word: '', dictionary: null, host: null, allowed: true }
          : rulingOf(submission);
      const answer = ruling.word.toUpperCase();
      const out = answer.length > 0 && !ruling.allowed;
      const declared = view.declaredOf(player.id);

      return {
        player,
        submission,
        answer,
        ruling,
        out,
        // The declared length only when it is a second fact. Where it agrees
        // with the word it is the word's own length counted a second time, and
        // as a bare number on a row that ends in another bare number it read as
        // the score twice — which is exactly how it was reported from a phone.
        claimed: declared !== null && declared !== answer.length ? declared : null,
        score: submission?.score ?? 0,
      };
    });

  const newest = released[released.length - 1] ?? null;
  const size: RowSize = ordered.length > 6 ? 'sm' : ordered.length > 4 ? 'md' : 'lg';

  const listRef = useInsertion(rows.map((row) => row.player.id).join('>'));

  return (
    <Frame dense className={STAGE_FILL}>
      {/* A count of how many were good, how many were ruled out and how many
          were nothing used to sit up here. Every one of those numbers is
          countable from the rows underneath it, on the same screen, at the same
          moment. */}
      <header className="flex shrink-0 flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold sm:text-xl">The words</h2>
      </header>

      {/* Labelled, because a screen reader landing on the second list of names
          on this screen has to be told which one it is: the rail above is the
          running order and this is the round. */}
      <ul aria-label="The answers, best first" ref={listRef} className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const spotlit = row.player.id === newest;
          const nothing = row.answer.length === 0;
          // **Yours, drawn only on your own screen.** `youId` is local, so this
          // is nine different panels in a room of nine, each with one row edged.
          // The complaint it answers is that finding your own answer in a list
          // that is deliberately not in seat order takes a moment, and the
          // moment is the one where the room is reacting to it.
          const mine = row.player.id === state.youId;

          return (
            <li
              key={row.player.id}
              data-player-id={row.player.id}
              data-spotlit={spotlit ? 'true' : undefined}
              data-mine={mine ? 'true' : undefined}
              // The border is on every row, transparent when it says nothing, so
              // a ruling arriving cannot move the rows under it by two pixels.
              // One wash and no more: the row sits on the frame's opaque fill.
              //
              // **A ruled-out row no longer gets a red outline, and losing it
              // was a cut rather than an oversight.** The row already says it
              // twice — the word is struck through and the chip beside it names
              // the reason — and the outline was a third telling at the size of
              // the whole row, which made the answers that scored nothing the
              // loudest things on a panel whose entire point is the best one.
              // The only thing left that colours a whole row is the spotlight,
              // which says *this one just landed* and is about the pacing.
              // The `you` edge is an inset shadow rather than a border or an
              // outline: it draws inside the box the row already occupies, so it
              // cannot move the rows under it, and it stacks with the spotlight
              // fill instead of replacing it — your own answer landing is both
              // things at once, and it is the case that matters most.
              className={`flex items-center rounded-card border transition-colors duration-300 ease-out ${
                LEADER_ROW[size]
              } ${spotlit ? 'border-blue-500/60 bg-blue-700/35' : 'border-transparent bg-surface-2'} ${
                mine ? 'shadow-[inset_0_0_0_2px_var(--c-accent)]' : ''
              }`}
            >
              {/* Two lines, and `min-w-0` so a long name is truncated inside the
                  chip rather than shoving the score off the end of the row. The
                  chip is the identity everywhere else in the game — the dot, the
                  name and the handle, guest or bot badge — so a display name
                  shaped like somebody else's handle cannot read as that player
                  here either. */}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {/* The thinking toggle rides the identity line rather than the
                    right-hand end of the row, and that is a measurement rather
                    than a preference: out there it is `shrink-0` beside the
                    score and the ruling button, and on a 375px phone the three
                    of them left an AI's word 38 pixels — ELEPHANT came out as
                    two letters and an ellipsis beside a chip saying it was not
                    on the board. It belongs on this line anyway. It is a fact
                    about what kind of player this is, which is what the rest of
                    the line already says. */}
                <div className="flex min-w-0 items-center gap-2">
                  <PlayerChip
                    player={row.player}
                    color={displayColorOf(state, row.player)}
                    size={LEADER_CHIP[size]}
                    showIdentity={false}
                  />
                  {row.submission === undefined ? null : (
                    <ReasoningNote player={row.player} submission={row.submission} />
                  )}
                </div>

                <div className="flex min-w-0 items-center gap-1">
                  {/* Nothing is laid over the word. A ruled-out one is struck
                      through by its own text run and keeps full contrast: the
                      strike says the ruling, the chip says why, and neither can
                      cover a neighbour. `skip-ink: none` so the line crosses the
                      descenders rather than breaking around them, which at this
                      weight reads as a dropped stroke.

                      **The word is also the control that asks what it means**,
                      and it keeps every one of those faces while being one: the
                      button wraps the same text run, so the strike is still
                      drawn by the glyphs it crosses and the truncation is still
                      the word's own. It carries no padding, which is what keeps
                      a row the height it was — the row scale here is a table
                      keyed on the seat count precisely because a row already
                      read must never resize.

                      A ruled-out word is askable like any other. `not a word` is
                      this game's verdict against its own list, and whether a
                      dictionary has heard of it is exactly what the player who
                      typed it asks next. A row with nothing on it is a dash and
                      is not a word at all. */}
                  {nothing ? (
                    <span
                      className={`min-w-0 truncate font-extrabold tracking-[0.06em] ${LEADER_WORD[size]} text-ink-dim`}
                    >
                      —
                    </span>
                  ) : (
                    <AskableWord
                      view={view}
                      word={row.answer}
                      className={`min-w-0 truncate font-extrabold tracking-[0.06em] text-ink ${
                        LEADER_WORD[size]
                      } ${
                        row.out
                          ? 'line-through decoration-alert/80 decoration-[3px] [text-decoration-skip-ink:none]'
                          : ''
                      }`}
                    >
                      {row.answer}
                    </AskableWord>
                  )}

                  {/* Two questions, and the answers are chips rather than a
                      sentence: who decided, and why the word did not stand.
                      They are usually not both on a row — a host who overturns
                      the book leaves nothing for the book to say, and a book
                      nobody argued with had no host. Both appear on the one case
                      that is genuinely two facts: a ruling that CONFIRMED a
                      rejection, where `host ruled` alone would leave the room
                      wondering what the word did wrong. */}
                  {row.ruling.host !== null ? (
                    <ReasonChip tone="accent">host ruled</ReasonChip>
                  ) : null}

                  {row.out && row.ruling.dictionary !== true ? (
                    <ReasonChip tone="alert">{ruledOutLabel(view.round, row.ruling)}</ReasonChip>
                  ) : null}

                  {row.claimed === null ? null : (
                    <ReasonChip tone="dim">claimed {row.claimed}</ReasonChip>
                  )}
                </div>
              </div>

              {/* Said once, and centred against both lines rather than against
                  either of them. */}
              <span
                className={`tabular shrink-0 font-mono font-bold ${LEADER_SCORE[size]} ${
                  row.score > 0 ? 'text-good' : 'text-ink-dim'
                }`}
              >
                {row.score}
              </span>

              <RulingControl
                view={view}
                player={row.player}
                answer={row.answer}
                ruling={row.ruling}
              />
            </li>
          );
        })}
      </ul>
    </Frame>
  );
}
